import { makeAutoObservable } from 'mobx'
import { RootStore } from './RootStore'
import { CurrentTierUsage, TierLimits, TierUsageClient } from 'api/clients/TierUsageClient'
import { FetchSubscriptionResponse, WorkspaceClient } from 'api/clients/WorkspaceClient'
import {
  fetchAutopilotAddOns,
  fetchAutopilotPlans,
  fetchPaymentPlans,
  PaymentPlan,
  PaymentPlanKey,
} from 'api/clients/PaymentPlanClient'
import { EARLY_ADOPTER_PLAN } from 'hooks/usePaymentPlans'
import { AutopilotPlan, VirtualMarketerPlan } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/types'

export class TierUsageStore {
  tierInfo: CurrentTierUsage | null = null
  tierLimits: TierLimits | null = null
  currentSubscription: FetchSubscriptionResponse | null = null
  paymentPlans: PaymentPlan[] = []
  autopilotPlans: AutopilotPlan[] = []
  autopilotAddOns: AutopilotPlan[] = []
  loading = false
  rootStore: RootStore
  initialized = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  setPaymentPlans = (paymentPlans: PaymentPlan[]) => {
    this.paymentPlans = paymentPlans
  }

  setAutopilotPlans = (autopilotPlans: VirtualMarketerPlan[]) => {
    this.autopilotPlans = autopilotPlans
  }

  setAutopilotAddOns = (autopilotAddOns: AutopilotPlan[]) => {
    this.autopilotAddOns = autopilotAddOns
  }

  setSubscription = (subscription: FetchSubscriptionResponse) => {
    this.currentSubscription = subscription
  }

  setTierInfo = (tierInfo: CurrentTierUsage) => {
    this.tierInfo = tierInfo
  }

  setTierLimits = (tierLimits: TierLimits) => {
    this.tierLimits = tierLimits
  }

  setLoading = (loading: boolean) => {
    this.loading = loading
  }

  setInitialized = (initialized: boolean) => {
    this.initialized = initialized
  }

  /**
   * Fetches tier usage, subscription, and payment plans data.
   * Only runs once per app load. For forced refresh (e.g., after upgrade), use refetch().
   */
  fetch = async (workspaceId: number) => {
    // Skip if already initialized or currently loading to prevent duplicate requests
    if (this.initialized || this.loading) return

    await this._doFetch(workspaceId)
  }

  /**
   * Force refetch of all data, bypassing the initialization guard.
   * Use this after actions that change subscription state (e.g., upgrade, cancel).
   */
  refetch = async (workspaceId: number) => {
    if (this.loading) return

    // Reset initialized to allow refetch
    this.initialized = false
    await this._doFetch(workspaceId)
  }

  private _doFetch = async (workspaceId: number) => {
    this.loading = true

    const [tierUsageRes, currentSubRes, paymentPlansRes, autopilotPlansRes, autopilotAddOnsRes] = await Promise.all([
      TierUsageClient.getTierUsage(workspaceId),
      WorkspaceClient.fetchSubscription(workspaceId),
      fetchPaymentPlans(),
      fetchAutopilotPlans(),
      fetchAutopilotAddOns(),
    ])

    if (tierUsageRes.type === 'success') {
      this.setTierInfo(tierUsageRes.data.tier_usage)
      this.setTierLimits(tierUsageRes.data.tier_usage?.price_meta)
    }

    if (currentSubRes.type === 'success') {
      this.setSubscription(currentSubRes.data)
    }

    if (paymentPlansRes.type === 'success' && paymentPlansRes.data.payment_plans) {
      this.setPaymentPlans([
        ...paymentPlansRes.data.payment_plans,
        EARLY_ADOPTER_PLAN,
        {
          id: Infinity,
          name: 'Enterprise',
          key: PaymentPlanKey.BlazeEnterprise,
          price: {
            month: {
              price_id: 'fake_id',
              unit_amount: 399,
            },
            year: {
              price_id: 'fake_id',
              unit_amount: 3990,
            },
          },
          member_limit: Infinity,
          member_minimum: 1,
          doc_limit: Infinity,
          base_price_meta: {},
        },
      ])
    }

    if (autopilotPlansRes.type === 'success' && autopilotPlansRes.data.plans) {
      this.setAutopilotPlans(autopilotPlansRes.data.plans)
    }

    if (autopilotAddOnsRes.type === 'success' && autopilotAddOnsRes.data.add_ons) {
      this.setAutopilotAddOns(autopilotAddOnsRes.data.add_ons)
    }

    this.setLoading(false)
    this.setInitialized(true)
  }
}
